import React, {useRef, useEffect, useState} from 'react';
import {fabric} from 'fabric';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import Arrow from './AnnotationShapes/Arrow';
import Constants, {AnnotationColors, AnnotationModes, KeyCodes} from '../../constants/Constants';
import {IComment} from '../../model/commentModel';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {selectedConversationStore} from '../../context/selectedConversationProvider';
import {updateCanvasState} from '../../context/canvasProvider';
import {annotationManagerStore} from '../../context/annotationManagerProvider';
import {commentsStore} from '../../context/commentsProvider';
import {AriaAlert} from '../util/StyledElements';
import {updateQuizData} from '../../context/quizDataProvider';
import {mediaStore} from '../../context/mediaProvider';
import logging from '../../service/logging';
import {importMediaInterface} from '../../hooks/dynamicImport';

const editingInputTypes = [Constants.elementInputTypes.input, Constants.elementInputTypes.textArea, Constants.elementInputTypes.search];

export const AnnotationWrapper = styled.div<{pointerEvents: string}>`
   position: relative;
   width: 100%;
   height: 100%;
   pointer-events: ${props => props.pointerEvents};
`;

export interface IStateMappedProps {
   SelectedComment?: IComment;
   annotationMode: AnnotationModes;
   annotationColor: AnnotationColors;
   comments: IComment[];
   IsImage: boolean;
   Width: number;
}

export const AnnotationBase: React.FC<IStateMappedProps> = ({SelectedComment, annotationMode, annotationColor, comments, IsImage, Width}) => {
   const {t} = useTranslation();

   const [showAnnotationAccessibilityAlert, setShowAnnotationAccessibilityAlert] = useState(false);
   const [style, setStyle] = useState({});
   const useLegacyScaleRef = useRef(false);
   const canvasRef = useRef<fabric.Canvas>(null);
   const mediaContentRef = useRef(document.querySelector('#tscMediaContent .video-div') ?? document.querySelector('#tscMediaContent .smart-player-image-media'));

   const onFabricObjectLoad = (jsonToIgnore: any, object: fabric.Object): void => {
      object.set({
         selectable: false,
         hoverCursor: Constants.cursors.default,
         opacity: 0,
         strokeWidth: canvasRef.current.freeDrawingBrush.width
      });

      object.animate('opacity', 1, {
         duration: Constants.annotation.animationDuration,
         onChange: canvasRef.current.requestRenderAll.bind(canvasRef.current)
      });
   };

   useEffect(() => {
      let timeoutId = -1;

      if (showAnnotationAccessibilityAlert) {
         document.getElementById('annotation-exists-alert').style.display = 'block';

         timeoutId = window.setTimeout(() => {
            document.getElementById('annotation-exists-alert').style.display = 'none';
            setShowAnnotationAccessibilityAlert(false);
         }, 1000);
      }

      return () => {
         clearTimeout(timeoutId);
      };
   }, [showAnnotationAccessibilityAlert]);

   const loadAnnotations = (): void => {
      const annotation = SelectedComment?.parentCommentId ? comments.find(c => c.id === SelectedComment.parentCommentId).annotation : SelectedComment?.annotation;

      if (!annotation) {
         scaleCanvas();
         return;
      }

      setShowAnnotationAccessibilityAlert(true);
      canvasRef.current.clear();
      useLegacyScaleRef.current = !annotation.includes(Constants.annotation.newScaleAnnotationFlag);
      scaleCanvas();

      try {
         canvasRef.current.loadFromJSON(annotation, () => {
            // Empty on purpose
         }, onFabricObjectLoad);
      } catch (error) {
         logging.error(`Something went wrong loading annotation data: ${annotation}`, error);
      }
   };

   const removeOutOfViewObject = (event: any): void => {
      if (!event.target) {
         return;
      }

      const object = event.target as fabric.Object;
      const bounds = object.getBoundingRect();
      const pastLeft = bounds.left + bounds.width < 0;
      const pastTop = bounds.top + bounds.height < 0;
      const pastRight = bounds.left > canvasRef.current.width;
      const pastBottom = bounds.top > canvasRef.current.height;

      if (pastLeft || pastTop || pastRight || pastBottom) {
         canvasRef.current.remove(object);
      }
   };

   const scaleCanvas = (): void => {
      const scale = useLegacyScaleRef.current ?
         Math.max(mediaContentRef.current.clientWidth, mediaContentRef.current.clientHeight) / Constants.annotation.scale :
         mediaContentRef.current.clientWidth / Width;

      const strokeWidth = 1 / scale * Constants.annotation.strokeWidth;

      canvasRef.current.setZoom(scale);
      canvasRef.current.freeDrawingBrush.width = strokeWidth;
      canvasRef.current.getObjects().forEach((obj: fabric.Object) => {
         obj.strokeWidth = strokeWidth;
      });
   };

   useEffect(() => {
      if (!canvasRef.current) {
         return;
      }

      canvasRef.current.freeDrawingBrush.color = annotationColor;
      canvasRef.current.getActiveObjects().forEach((object: fabric.Object) => {
         object.stroke = annotationColor;
         if (object.fill !== Constants.annotation.transparentColor) {
            object.fill = annotationColor;
         }
      });

      canvasRef.current.requestRenderAll();
   }, [annotationColor]);

   const onKeyDown = React.useCallback((e: KeyboardEvent) => {
      if (!canvasRef.current || annotationMode !== AnnotationModes.edit) {
         return;
      }

      const isInputSelected = editingInputTypes.some(type => (e.target as HTMLInputElement).type === type);
      const isRemoveEvent = e.keyCode === KeyCodes.backspace || e.keyCode === KeyCodes.delete;

      if (isInputSelected || !isRemoveEvent) {
         return;
      }

      e.preventDefault(); // Do not trigger navigation back if we are deleting an object

      canvasRef.current.getActiveObjects().forEach(object => canvasRef.current.remove(object));
   }, [annotationMode]);

   useEffect(() => {
      if (!canvasRef.current) {
         return;
      }

      canvasRef.current.clear();

      void importMediaInterface().then(mediaInterface => {
         if (annotationMode === AnnotationModes.edit) {
            mediaInterface.singleClickControlEnabled = false;
            mediaInterface.doubleClickControlEnabled = false;
            mediaInterface.pause();
         } else {
            mediaInterface.singleClickControlEnabled = true;
            mediaInterface.doubleClickControlEnabled = true;
         }

         if (annotationMode === AnnotationModes.display) {
            canvasRef.current.isDrawingMode = false;
            loadAnnotations();
         }

         canvasRef.current.selection = annotationMode === AnnotationModes.edit;
      });
   }, [SelectedComment?.annotation, annotationMode]);

   const setupCanvas = () => {
      fabric.Object.prototype.padding = Constants.annotation.controlsPadding;
      fabric.Object.prototype.centeredRotation = false;
      fabric.Object.prototype.centeredScaling = false;
      fabric.Object.prototype.strokeUniform = true;
      fabric.Object.prototype.objectCaching = false;
      fabric.Group.prototype.hasControls = false;

      const canvas = new fabric.Canvas('canvas-element', {
         uniformScaling: false,
         selection: annotationMode === AnnotationModes.edit,
         defaultCursor: 'pointer',
         width: mediaContentRef.current.clientWidth,
         height: mediaContentRef.current.clientHeight,
         perPixelTargetFind: true,
         enableRetinaScaling: false
      });

      canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      canvas.freeDrawingBrush.color = annotationColor;

      canvas.on('object:modified', removeOutOfViewObject);
      canvas.on('object:added', removeOutOfViewObject);

      Arrow.initializeEventListeners(canvas);

      canvasRef.current = canvas;
      updateCanvasState({canvasRef: canvas});
   };

   const resetDimensions = () => {
      if (mediaContentRef.current.clientWidth === 0 || mediaContentRef.current.clientHeight === 0) {
         return;
      }

      if (!canvasRef.current) {
         setupCanvas();
         loadAnnotations();
      } else if (canvasRef.current.width !== mediaContentRef.current.clientWidth || canvasRef.current.height !== mediaContentRef.current.clientHeight) {
         canvasRef.current.setDimensions({width: mediaContentRef.current.clientWidth, height: mediaContentRef.current.clientHeight});
         scaleCanvas();
      }
   };

   useEffect(() => {
      resetDimensions(); // Though observers execute on load, this helps headless tests with no actual dimensions
      const observer = new ResizeObserver(resetDimensions);
      observer.observe(mediaContentRef.current);

      return () => {
         observer.disconnect();
         updateQuizData({IsQuizzing: false});
         if (canvasRef.current) {
            canvasRef.current.dispose();
         }
      };
   }, []);

   useEffect(() => {
      window.addEventListener('keydown', onKeyDown);
      return () => {
         window.removeEventListener('keydown', onKeyDown);
      };
   }, [onKeyDown]);

   useEffect(() => {
      const updatedStyle: any = {
         width: '100%',
         height: '100%',
         margin: 'auto'
      };

      if (IsImage) {
         updatedStyle.position = 'absolute';
         updatedStyle.left = 0;
         updatedStyle.right = 0;
         updatedStyle.top = 0;
         updatedStyle.bottom = 0;
      }

      setStyle(updatedStyle);
   }, [IsImage]);

   return (
      <>
         <AnnotationWrapper
            pointerEvents={annotationMode === AnnotationModes.edit ? 'auto' : 'none'}
            data-test-id="annotation-wrapper"
         >
            <canvas
               id="canvas-element"
               data-test-id="annotation-canvas"
               style={style}
            />
         </AnnotationWrapper>
         <AriaAlert
            role="alert"
            id="annotation-exists-alert"
         >
            {t('annotation.annotationExistsAlert')}
         </AriaAlert>
      </>
   );
};

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {SelectedComment}: Partial<IStateMappedProps>,
   {annotationMode, annotationColor}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {IsImage, Width}: Partial<IStateMappedProps>
): IStateMappedProps => ({SelectedComment, annotationMode, annotationColor, comments, IsImage, Width});

export default withMemoizedContexts(mapStatesToProps, selectedConversationStore, annotationManagerStore, commentsStore, mediaStore)(AnnotationBase);
